/**
 * M3-T6 ReceiveGoodsPage (issue #611).
 *
 * Tablet-optimized PO receipt entry page. Operator workflow:
 *   1. Page mounts with the PO header + lines.
 *   2. Operator focuses one line at a time, types qty via the
 *      large on-screen numeric keypad, presses Enter / "Next" to
 *      advance to the next short line.
 *   3. When done, "Submit receipt" sends the batch in a single POST.
 *      Backend transitions the PO to `partially_received` or
 *      `received` atomically.
 *
 * Tablet ergonomics:
 *   - Touch targets ≥ 48px height (Tailwind h-12 = 48px, h-14 = 56px).
 *   - Numeric keypad lives on the page (not the OS keyboard) so the
 *     operator does not have to dismiss/recall the soft keyboard for
 *     every line.
 *   - Single-PO, single-line focus pattern — no multi-PO multitask.
 *
 * Decimal-string discipline (ADR-0003): backend `qty_ordered` /
 * `qty_received` are NUMERIC(12,3)-as-text. The keypad state is
 * stored as a string so we don't accumulate IEEE-754 jitter; only
 * the final submit converts to `number` via `Number(...)` at the
 * boundary (the route's zod schema is `z.number().positive()`).
 *
 * Permission gate is route-level (`INGREDIENTS_WRITE`).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Delete, RotateCcw } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageShell,
  Button,
  Loading,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { usePurchaseOrderDetail, useRecordReceipt } from '@features/procurement';
import type { PurchaseOrderItem } from '@/services';
import { formatNumber } from '@lib/utils';
import { parseDecimalText, normalizeDecimalText } from '@/services';

// Big-key numeric layout used by the on-screen keypad. The layout
// mirrors a standard calculator (7-8-9 top, 0 bottom) so muscle
// memory carries over.
const KEYPAD_KEYS: Array<string> = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0'];

interface LineState {
  /** Operator-typed value (raw keypad input). Empty until typed. */
  raw: string;
  /** True once operator commits this line (presses "Next"). */
  done: boolean;
}

/** Open lines = receipt-pending (qty_received < qty_ordered). Closed
 *  lines do not appear in the entry list — they're displayed
 *  read-only in the header summary. Both sides go through
 *  `parseDecimalText` (the ADR-0003 SSOT helper) so the comparison
 *  uses the same parse path as overflow / display, avoiding the
 *  class-wide "no bare Number() on Decimal-string columns" footgun
 *  that bit #628's hotfix. */
function isOpen(item: PurchaseOrderItem): boolean {
  const received = parseDecimalText(item.qty_received, `ReceiveGoodsPage:${item.id}:qty_received`);
  const ordered = parseDecimalText(item.qty_ordered, `ReceiveGoodsPage:${item.id}:qty_ordered`);
  return received < ordered;
}

function remainingQty(item: PurchaseOrderItem): number {
  return (
    parseDecimalText(item.qty_ordered, `ReceiveGoodsPage:${item.id}:qty_ordered`) -
    parseDecimalText(item.qty_received, `ReceiveGoodsPage:${item.id}:qty_received`)
  );
}

export function ReceiveGoodsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id: poId } = useParams<{ id: string }>();
  const detail = usePurchaseOrderDetail(poId);
  const submit = useRecordReceipt(poId ?? '');

  const items: PurchaseOrderItem[] = detail.data?.items ?? [];
  const po = detail.data?.purchase_order;

  // Open lines drive the entry workflow. Memoised so the
  // `activeIndex` cursor stays stable across renders.
  const openLines = useMemo(() => items.filter(isOpen), [items]);

  // Per-line entry state, keyed by po_item_id. Seeded lazily when
  // the line becomes the active focus.
  const [state, setState] = useState<Record<string, LineState>>({});
  const [activeIndex, setActiveIndex] = useState(0);

  const activeLine = openLines[activeIndex];
  const activeState = activeLine ? (state[activeLine.id] ?? { raw: '', done: false }) : null;

  const setActiveRaw = useCallback(
    (next: string) => {
      if (!activeLine) return;
      setState((prev) => ({
        ...prev,
        [activeLine.id]: { raw: next, done: false },
      }));
    },
    [activeLine],
  );

  const press = useCallback(
    (key: string) => {
      if (!activeLine || !activeState) return;
      if (key === '.' && activeState.raw.includes('.')) return;
      // Cap at 3 decimal places (NUMERIC(12,3) precision floor).
      const dotIdx = activeState.raw.indexOf('.');
      if (dotIdx !== -1 && activeState.raw.length - dotIdx > 3) return;
      setActiveRaw(activeState.raw + key);
    },
    [activeLine, activeState, setActiveRaw],
  );

  const backspace = useCallback(() => {
    if (!activeState) return;
    setActiveRaw(activeState.raw.slice(0, -1));
  }, [activeState, setActiveRaw]);

  const clear = useCallback(() => {
    setActiveRaw('');
  }, [setActiveRaw]);

  const fillRemaining = useCallback(() => {
    if (!activeLine) return;
    // Format via .toFixed(3) + strip trailing zeros so the keypad
    // displays a clean Decimal-string instead of a 17-digit IEEE-754
    // float jitter string (e.g. `4.499999999999999`). Mirrors the
    // backend `formatNumericText` strip-trailing-zero contract from
    // `purchase-order-service.ts`.
    const n = remainingQty(activeLine);
    const formatted = Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, '');
    setActiveRaw(formatted);
  }, [activeLine, setActiveRaw]);

  const commitAndNext = useCallback(() => {
    if (!activeLine || !activeState) return;
    // Normalize keypad artifacts (`.5` / `5.`) to the ADR-0003 wire
    // contract before the value is committed to `receipts`. The backend
    // validates each `qty` against `/^-?\d+(\.\d+)?$/` and fails the
    // whole batch atomically on the first reject, so a lenient value
    // must never reach the wire.
    const normalized = normalizeDecimalText(activeState.raw);
    if (normalized === null) {
      toast.error(t('procurement.receive.invalidQty'));
      return;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(t('procurement.receive.invalidQty'));
      return;
    }
    if (parsed > remainingQty(activeLine) + 1e-6) {
      toast.error(t('procurement.receive.overflow'));
      return;
    }
    setState((prev) => ({
      ...prev,
      [activeLine.id]: { raw: normalized, done: true },
    }));
    setActiveIndex((i) => Math.min(i + 1, openLines.length));
  }, [activeLine, activeState, openLines.length, t, toast]);

  // Keyboard fallback so a hardware keyboard (e.g. dev laptop)
  // also works for testing — tablets without keyboards use the
  // on-screen keypad. Listens at document level so focus inside
  // a form input does not need to be wired manually. Keys typed into
  // an open dialog (e.g. the Cmd/Ctrl+K palette) belong to it (#2477).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof Element && e.target.closest('[role="dialog"]')) return;
      if (e.key >= '0' && e.key <= '9') press(e.key);
      else if (e.key === '.') press('.');
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Enter') commitAndNext();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [press, backspace, commitAndNext]);

  if (!poId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {t('procurement.receive.missingId')}
        </CardContent>
      </Card>
    );
  }
  if (detail.isLoading) return <Loading />;
  if (detail.isError || !po) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {t('procurement.receive.loadError')}
        </CardContent>
      </Card>
    );
  }

  // Receipt lines carry `qty` as a decimal-text string straight from
  // the keypad — no `Number(...)` round-trip, so the backend's
  // NUMERIC(12,3) column receives the exact typed value with no
  // IEEE-754 re-serialisation jitter (ADR-0003).
  const receipts = openLines
    .filter((l) => {
      const s = state[l.id];
      return s?.done && s.raw !== '';
    })
    .map((l) => ({
      item_id: l.id,
      qty: state[l.id].raw,
    }));

  const canSubmit = receipts.length > 0 && !submit.isPending;

  const handleSubmit = async () => {
    try {
      const updated = await submit.mutateAsync({ receipts });
      toast.success(
        t('procurement.receive.submitSuccess', {
          status: t(`procurement.status.${updated.status}`),
        }),
      );
      navigate('/procurement/purchase-orders');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <PageShell
      data-testid="procurement-receive-page"
      title={t('procurement.receive.title')}
      subtitle={t('procurement.receive.subtitle', { id: po.id })}
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('procurement.receive.lines')}</CardTitle>
          </CardHeader>
          <CardContent>
            {openLines.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('procurement.receive.allReceived')}
              </p>
            ) : (
              <ul className="space-y-2">
                {openLines.map((line, idx) => {
                  const s = state[line.id];
                  const isActive = idx === activeIndex;
                  const isDone = s?.done;
                  return (
                    <li
                      key={line.id}
                      className={`flex items-center justify-between gap-3 p-3 rounded-md border min-h-12 ${
                        isActive
                          ? 'border-primary bg-primary/5'
                          : isDone
                            ? 'border-success/40 bg-success/10'
                            : 'border-border'
                      }`}
                      data-testid={`receive-line-${line.id}`}
                    >
                      <button
                        type="button"
                        className="flex-1 text-left h-12 flex flex-col justify-center"
                        onClick={() => setActiveIndex(idx)}
                      >
                        <span className="font-medium">{line.ingredient_id}</span>
                        <span className="text-xs text-muted-foreground">
                          {t('procurement.receive.linePrompt', {
                            remaining: formatNumber(remainingQty(line), {
                              maximumFractionDigits: 3,
                            }),
                            unit: line.unit,
                          })}
                        </span>
                      </button>
                      <div className="text-right tabular-nums min-w-[80px]">
                        <div className="text-lg font-semibold">{s?.raw || '—'}</div>
                        <div className="text-xs text-muted-foreground">{line.unit}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {activeLine
                ? t('procurement.receive.keypadFor', {
                    ingredient: activeLine.ingredient_id,
                  })
                : t('procurement.receive.keypadIdle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-14 rounded-md border bg-background flex items-center justify-end px-4 text-3xl tabular-nums font-semibold">
              {activeState?.raw || '0'}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {KEYPAD_KEYS.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  className="h-14 text-xl"
                  onClick={() => press(key)}
                  disabled={!activeLine}
                  data-testid={`keypad-${key === '.' ? 'dot' : key}`}
                  aria-label={t('procurement.receive.keypadKey', { key })}
                >
                  {key}
                </Button>
              ))}
              <Button
                variant="outline"
                className="h-14"
                onClick={backspace}
                disabled={!activeLine}
                data-testid="keypad-backspace"
                aria-label={t('procurement.receive.backspace')}
              >
                <Delete className="w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="ghost"
                className="h-12"
                onClick={clear}
                disabled={!activeLine}
                data-testid="keypad-clear"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('procurement.receive.clear')}
              </Button>
              <Button
                variant="ghost"
                className="h-12"
                onClick={fillRemaining}
                disabled={!activeLine}
                data-testid="keypad-fill"
              >
                {t('procurement.receive.fill')}
              </Button>
            </div>
            <Button
              className="w-full h-14 text-base"
              onClick={commitAndNext}
              disabled={!activeLine || !activeState?.raw}
              data-testid="keypad-next"
            >
              {t('procurement.receive.next')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          className="h-12"
          onClick={() => navigate('/procurement/purchase-orders')}
        >
          {t('procurement.receive.cancel')}
        </Button>
        <Button
          className="h-12 px-8"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="receive-submit"
        >
          {t('procurement.receive.submit', { count: receipts.length })}
        </Button>
      </div>
    </PageShell>
  );
}

export default ReceiveGoodsPage;
